import { DependencyList, EffectCallback, useEffect } from "react";

/**
 * Run work after the page has drawn, rather than while it is drawing.
 *
 * A stock page fires around forty requests on mount. They are not equally
 * urgent: measured on a cold load of `/s/NYSE/AMC`, the two slowest calls on
 * the page - the Stock Report and Trading Idea existence probes, a second each
 * - decide whether a tab appears in the strip and whether a pill appears next
 * to the company name. Nothing above the fold waits on either answer, but both
 * left the browser in the same burst as the chart's data and competed with it
 * for the connection and for the backend's workers.
 *
 * So: same effect, same cleanup, started once the browser has nothing better
 * to do. `requestIdleCallback` is the right primitive and every browser we
 * support has it except Safari, which gets a frame-then-task fallback that
 * lands after the first paint for the same reason.
 *
 * WHAT NOT TO PUT HERE. Anything the reader is looking at. Deferring a request
 * whose result is visible content does not make the page faster, it moves the
 * spinner - the news grid and the commentary block stay where they are. This
 * is for work that is invisible until it arrives.
 */

/**
 * Upper bound on the wait, in ms.
 *
 * `requestIdleCallback` with no timeout can be postponed indefinitely on a
 * busy page, and a background tab never goes idle at all because it stops
 * getting frames. Both cases have to end with the work being done, so
 * everything scheduled here carries a deadline.
 */
const IDLE_TIMEOUT_MS = 2000;

type Cancel = () => void;

/**
 * The two idle-scheduling calls, as optional members.
 *
 * Not an interface extending `Window`: lib.dom declares both as required, so
 * an interface that widens them to optional does not extend it, and one that
 * does not widen them cannot express the browser that lacks them. A cast at
 * the point of use says exactly what is meant - these may or may not be here.
 */
type IdleApi = {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
};

/**
 * Queue `fn` for the browser's next idle period, and return a canceller.
 *
 * Server-side there is no paint to stay out of the way of and no idle
 * callback: the work is dropped, which is what every caller here wants, since
 * they all run in effects that never fire during SSR anyway.
 */
export const scheduleIdle = (fn: () => void, timeout: number = IDLE_TIMEOUT_MS): Cancel => {
    if (typeof window === "undefined") return () => undefined;
    const w = window as unknown as IdleApi;

    if (typeof w.requestIdleCallback === "function") {
        const handle = w.requestIdleCallback(fn, { timeout });
        return () => w.cancelIdleCallback?.(handle);
    }

    // Safari. A frame callback runs before the paint it belongs to, so the
    // task scheduled from inside it is the first thing after that paint;
    // the timer alongside it covers a background tab, where no frame ever
    // comes. Whichever arrives first wins and the other becomes a no-op.
    let done = false;
    let task = 0;
    const run = () => {
        if (done) return;
        done = true;
        fn();
    };
    const frame = window.requestAnimationFrame(() => {
        task = window.setTimeout(run, 0);
    });
    const deadline = window.setTimeout(run, timeout);
    return () => {
        done = true;
        window.cancelAnimationFrame(frame);
        window.clearTimeout(task);
        window.clearTimeout(deadline);
    };
};

/**
 * `useEffect`, deferred to the browser's next idle period.
 *
 * Identical contract otherwise: the effect body runs on mount and whenever
 * `deps` change, and whatever it returns is called on cleanup. If the deps
 * change (or the component unmounts) before the browser ever went idle, the
 * effect simply never runs - which is the desired behaviour for a probe about
 * a ticker the user has already navigated away from.
 */
export const useIdleEffect = (effect: EffectCallback, deps: DependencyList, timeout: number = IDLE_TIMEOUT_MS): void => {
    useEffect(() => {
        let cleanup: ReturnType<EffectCallback> = undefined;
        const cancel = scheduleIdle(() => {
            cleanup = effect();
        }, timeout);
        return () => {
            cancel();
            if (typeof cleanup === "function") cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};
